import React from 'react';
import { Box, Typography, Chip, Button } from '@mui/material';
import { IListEncuesta } from '../../models/encuestas';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BarChartIcon from '@mui/icons-material/BarChart';
import ReplayIcon from '@mui/icons-material/Replay';

interface ListEncuestasProps {
  listadoEncuestas: IListEncuesta[];
  selectEncuestaAction: (idEncuesta: number) => void;
  verResultadosAction: (idEncuesta: number, nombre: string) => void;
}

export const ListEncuestas = ({ listadoEncuestas, selectEncuestaAction, verResultadosAction }: ListEncuestasProps) => {
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f9', pb: 4 }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #013467 0%, #0a4a8a 100%)',
        px: 3, py: 4, mb: 3,
      }}>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
          Encuestas
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
          Seleccione una encuesta para responder
        </Typography>
      </Box>

      <Box sx={{ px: 2 }}>
        {listadoEncuestas.length === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center" mt={6}>
            No hay encuestas disponibles.
          </Typography>
        )}

        {listadoEncuestas.map(encuesta => {
          const respondida = encuesta.respondida === 1;
          const mostrarResultados = respondida && encuesta.resultados_Visibles === 1;
          const puedeReresponder = respondida && encuesta.segunda_Respuesta === 1;
          const bloqueada = respondida && !puedeReresponder;
          return (
            <Box
              key={encuesta.formulario}
              onClick={() => !bloqueada && selectEncuestaAction(encuesta.formulario)}
              sx={{
                backgroundColor: bloqueada ? '#f9f9f9' : '#fff',
                borderRadius: 2,
                mb: 2,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(1,52,103,0.08)',
                cursor: bloqueada ? 'default' : 'pointer',
                opacity: bloqueada ? 0.75 : 1,
                transition: 'transform 0.15s, box-shadow 0.15s',
                ...(!bloqueada && {
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(1,52,103,0.15)',
                  },
                }),
              }}
            >
              {/* Card top accent */}
              <Box sx={{ height: 4, background: bloqueada
                ? 'linear-gradient(90deg, #9e9e9e, #bdbdbd)'
                : 'linear-gradient(90deg, #013467, #FFCC00)',
              }} />

              <Box sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {/* Icon */}
                <Box sx={{
                  width: 44, height: 44, borderRadius: 2,
                  backgroundColor: bloqueada ? '#e8e8e8' : '#e8f0fb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {bloqueada
                    ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 22 }} />
                    : puedeReresponder
                      ? <ReplayIcon sx={{ color: '#013467', fontSize: 22 }} />
                      : <AssignmentIcon sx={{ color: '#013467', fontSize: 22 }} />
                  }
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: bloqueada ? '#777' : '#013467', lineHeight: 1.3, mb: 0.5 }}>
                    {encuesta.nombre}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#555', mb: 1.5, lineHeight: 1.4 }}>
                    {encuesta.descripcion}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <CalendarTodayIcon sx={{ fontSize: 12, color: '#888' }} />
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      {encuesta.desde} — {encuesta.hasta}
                    </Typography>
                    {respondida ? (
                      <Chip
                        label="Ya respondida"
                        size="small"
                        icon={<CheckCircleIcon sx={{ fontSize: '12px !important' }} />}
                        sx={{
                          ml: 1, height: 20, fontSize: 10,
                          backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600,
                          '& .MuiChip-icon': { color: '#2e7d32' },
                        }}
                      />
                    ) : (
                      <Chip
                        label="Activa"
                        size="small"
                        sx={{
                          ml: 1, height: 18, fontSize: 10,
                          backgroundColor: '#e6f4ea', color: '#2e7d32', fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {puedeReresponder && (
                      <Button
                        size="small"
                        startIcon={<ReplayIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          selectEncuestaAction(encuesta.formulario);
                        }}
                        sx={{
                          textTransform: 'none', fontWeight: 600, fontSize: 12,
                          color: '#fff', backgroundColor: '#013467', borderRadius: 1.5,
                          px: 1.5, py: 0.5,
                          '&:hover': { backgroundColor: '#0a4a8a' },
                        }}
                        variant="contained"
                      >
                        Volver a responder
                      </Button>
                    )}
                    {mostrarResultados && (
                      <Button
                        size="small"
                        startIcon={<BarChartIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          verResultadosAction(encuesta.formulario, encuesta.nombre);
                        }}
                        sx={{
                          textTransform: 'none', fontWeight: 600, fontSize: 12,
                          color: '#013467', borderColor: '#013467', borderRadius: 1.5,
                          px: 1.5, py: 0.5,
                        }}
                        variant="outlined"
                      >
                        Ver resultados
                      </Button>
                    )}
                  </Box>
                </Box>

                {bloqueada
                  ? <CheckCircleIcon sx={{ color: '#2e7d32', flexShrink: 0, mt: 0.5, fontSize: 20 }} />
                  : <ChevronRightIcon sx={{ color: '#bbb', flexShrink: 0, mt: 0.5 }} />
                }
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
